'use client'

import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type LazyExoticComponent,
} from 'react'
import { RichContent } from './RichContent'
import { CustomHtmlFrame } from './content/CustomHtmlFrame'
import { isCustomHtmlDocument, parseCustomHtmlDocument, serializeCustomHtmlDocument } from './serialize/customHtml'
import { escapeHtml } from './serialize/richElements'
import { defineRichElements } from './elements/registry'
import { detectDataDocumentKind } from './serialize/dataEnvelope'
import { richHtmlToPlainText } from './serialize/richHtml'
import {
  DEFAULT_RICH_EDIT_FEATURES,
  type RichEditMode,
  type RichEditPolicy,
  type RichEditPublicVariant,
  type RichEditProps,
  type RichEditVariant,
  type RichEditVariantProps,
} from './RichEditTypes'

const VARIANTS: Record<
  RichEditVariant,
  LazyExoticComponent<ComponentType<RichEditVariantProps>>
> = {
  std: lazy(() => import('./variants/std/StdRichEditor').then(module => ({ default: module.StdRichEditor }))),
  extended: lazy(() => import('./variants/extended/ExtendedRichEditor').then(module => ({ default: module.ExtendedRichEditor }))),
  blocks: lazy(() => import('./variants/blocks/BlocksRichEditor').then(module => ({ default: module.BlocksRichEditor }))),
  canvas: lazy(() => import('./variants/canvas/CanvasRichEditor').then(module => ({ default: module.CanvasRichEditor }))),
  contacts: lazy(() => import('./variants/contacts/ContactsRichEditor').then(module => ({ default: module.ContactsRichEditor }))),
}

const MODE_LABELS: Record<RichEditMode, string> = {
  plain: 'Pure text',
  std: 'Standard',
  extended: 'Extended',
  blocks: 'Blocks',
  canvas: 'Canvas',
}

const VARIANT_LABELS: Record<RichEditVariant, string> = {
  std: 'Standard',
  extended: 'Extended',
  blocks: 'Blocks',
  canvas: 'Canvas',
  contacts: 'Contacts',
}

function isPublicVariant(value: RichEditMode): value is RichEditPublicVariant {
  return value !== 'plain'
}

function fallbackPolicy(variant: RichEditVariant): RichEditPolicy {
  if (variant === 'contacts') {
    return { enabledModes: ['plain'], defaultMode: 'plain' }
  }
  return {
    enabledModes: ['plain', variant],
    defaultMode: variant,
  }
}

function detectStructuredVariant(value: string): RichEditVariant | null {
  if (detectDataDocumentKind(value) === 'contacts') return 'contacts'
  if (/<div\b[^>]*\bdesk_el=(?:"|')rich_canvas(?:"|')/i.test(value)) return 'canvas'
  if (/<div\b[^>]*\bdesk_el=(?:"|')rich_(?:layout|block)(?:"|')/i.test(value)) return 'blocks'
  return null
}

function initialRichVariant(
  value: string,
  configuredVariant: RichEditVariant,
  policy: RichEditPolicy,
  allowSpecializedVariant: boolean,
): RichEditVariant {
  const structured = detectStructuredVariant(value)
  if (structured && (structured !== 'contacts' || allowSpecializedVariant)) return structured
  if (isPublicVariant(policy.defaultMode)) return policy.defaultMode
  return policy.enabledModes.find(isPublicVariant) ?? (
    configuredVariant === 'contacts' ? 'extended' : configuredVariant
  )
}

export function RichEditHost({
  value,
  format,
  variant,
  onChange,
  onFormatChange,
  skin,
  features: featureOverrides,
  canonicalRef,
  syncKey,
  plainControl,
  toolbarAfter,
  modeButtonProps,
  plainTextareaProps,
  policy: policyProp,
  active = true,
  sessionKey,
}: RichEditProps) {
  const policy = policyProp ?? fallbackPolicy(variant)
  const allowSpecializedVariant = policyProp === undefined
  const features = useMemo(() => ({ ...DEFAULT_RICH_EDIT_FEATURES, ...featureOverrides }), [featureOverrides])
  const [documentValue, setDocumentValue] = useState(value)
  const customMode = format === 'html' && isCustomHtmlDocument(documentValue)
  const [customSource, setCustomSource] = useState(() => parseCustomHtmlDocument(value) ?? '')
  const [previewOpen, setPreviewOpen] = useState(false)
  const [inputError, setInputError] = useState('')
  const nativeDraft = useRef<{ value: string; format: 'plain' | 'html' } | null>(null)
  const customDraft = useRef<string | null>(null)
  const lastEmitted = useRef<string | null>(null)
  const [selectedVariant, setSelectedVariant] = useState<RichEditVariant>(() => (
    initialRichVariant(value, variant, policy, allowSpecializedVariant)
  ))
  const internalTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const surfaceRef = useRef<HTMLElement | null>(null)
  const Variant = VARIANTS[selectedVariant]
  const structuredVariant = detectStructuredVariant(documentValue)
  const protectsStructuredDocument = structuredVariant !== null && structuredVariant !== selectedVariant
  const activeMode: RichEditMode = format === 'plain'
    ? 'plain'
    : selectedVariant === 'contacts'
      ? policy.defaultMode
      : selectedVariant

  const canonicalValue = () => canonicalRef?.current?.value ?? internalTextareaRef.current?.value ?? documentValue

  const change = (next: string, reason: string) => {
    if (new TextEncoder().encode(next).byteLength > features.maxDocumentBytes) {
      setInputError('This document is too large. Shorten it before saving.')
      return false
    }
    setInputError('')
    lastEmitted.current = next
    setDocumentValue(next)
    onChange(next, { reason, variant: selectedVariant })
    return true
  }

  const selectAuthoringMode = (custom: boolean) => {
    if (custom === customMode) return
    setPreviewOpen(false)
    setInputError('')
    if (custom) {
      const current = canonicalValue()
      const source = customDraft.current ?? (format === 'plain' ? escapeHtml(current).replace(/\n/g, '<br>') : current)
      const next = serializeCustomHtmlDocument(source)
      if (new TextEncoder().encode(next).byteLength > features.maxDocumentBytes) {
        setInputError('This document is too large for Custom HTML.')
        return
      }
      nativeDraft.current = { value: current, format }
      setCustomSource(source)
      lastEmitted.current = next
      setDocumentValue(next)
      if (format !== 'html') onFormatChange('html', next)
      else onChange(next, { reason: 'custom-html:select', variant: selectedVariant })
    } else {
      let draft = nativeDraft.current
      if (!draft) {
        if (!window.confirm('Start a new MsgDesk design? Your Custom HTML draft will remain available until you close this editor.')) return
        draft = { value: '<p></p>', format: 'html' }
      }
      customDraft.current = customSource
      lastEmitted.current = draft.value
      setDocumentValue(draft.value)
      if (format !== draft.format) onFormatChange(draft.format, draft.value)
      else onChange(draft.value, { reason: 'msgdesk-design:select', variant: selectedVariant })
    }
  }

  const selectMode = (mode: RichEditMode) => {
    if (mode === 'plain') {
      if (format === 'plain') return
      if (
        structuredVariant
        && !window.confirm(`Convert this ${structuredVariant} document to pure text? Its structure will be removed.`)
      ) return
      const text = surfaceRef.current?.innerText ?? richHtmlToPlainText(documentValue)
      setDocumentValue(text)
      onFormatChange('plain', text)
      return
    }

    if (
      mode === 'canvas'
      && !structuredVariant
      && richHtmlToPlainText(canonicalValue()).trim()
      && !window.confirm('Start a Canvas document from this content? Rich formatting will be converted to Canvas text.')
    ) return

    setSelectedVariant(mode)
    if (format === 'plain') {
      const html = canonicalValue().replace(/(?:\r\n|\r|\n)/g, '<br />')
      setDocumentValue(html)
      onFormatChange('html', html)
    }
  }

  useEffect(() => {
    const next = canonicalValue() || value
    setDocumentValue(next)
    if (isCustomHtmlDocument(next) && next !== lastEmitted.current) {
      setCustomSource(parseCustomHtmlDocument(next) ?? '')
    }
  }, [syncKey, value, format])

  useEffect(() => {
    setSelectedVariant(initialRichVariant(
      canonicalValue() || value,
      variant,
      policy,
      allowSpecializedVariant,
    ))
    // Selection belongs to one editor session. Ordinary model projections must
    // not snap a user's Standard/Extended choice back to the path default.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey, policy, variant, allowSpecializedVariant])

  useEffect(() => {
    nativeDraft.current = null
    customDraft.current = null
    lastEmitted.current = null
    setPreviewOpen(false)
    setInputError('')
    setCustomSource(parseCustomHtmlDocument(canonicalValue() || value) ?? '')
    // Alternate drafts belong to this editor session only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey])

  useEffect(() => {
    if (features.customElements) defineRichElements()
  }, [features.customElements])

  const variantProps: RichEditVariantProps = {
    value: documentValue,
    onChange: change,
    skin,
    features,
    surfaceRef,
  }

  return (
    <div className={skin.root} data-rich-edit={selectedVariant} data-rich-format={format} data-rich-authoring={customMode ? 'custom-html' : 'msgdesk'}>
      <div className={skin.modeBar}>
        <button type="button" className={!customMode ? skin.modeButtonActive : skin.modeButton}
          aria-pressed={!customMode} onClick={() => selectAuthoringMode(false)}>MsgDesk Design</button>
        <button type="button" className={customMode ? skin.modeButtonActive : skin.modeButton}
          aria-pressed={customMode} onClick={() => selectAuthoringMode(true)}>Custom HTML</button>
      </div>
      <div className={skin.modeBar} hidden={customMode} style={customMode ? { display: 'none' } : undefined}>
        {policy.enabledModes.length > 1 ? policy.enabledModes.map(mode => (
          <button
            key={mode}
            type="button"
            {...(mode === 'plain' ? modeButtonProps : {})}
            className={activeMode === mode ? skin.modeButtonActive : skin.modeButton}
            aria-pressed={activeMode === mode}
            onClick={() => selectMode(mode)}
          >
            {MODE_LABELS[mode]}
          </button>
        )) : null}
        {!policy.enabledModes.includes('plain') || policy.enabledModes.length === 1 ? (
          <button
            type="button"
            {...modeButtonProps}
            hidden
            aria-hidden="true"
            tabIndex={-1}
          />
        ) : null}
        {toolbarAfter}
      </div>
      {inputError ? <p role="alert" className={skin.status}>{inputError}</p> : null}
      <div hidden={format !== 'plain'}>
        {plainControl ?? (
          <textarea
            {...plainTextareaProps}
            ref={internalTextareaRef}
            value={documentValue}
            onChange={event => change(event.currentTarget.value, 'plain:input')}
          />
        )}
      </div>
      <div hidden={format !== 'html'}>
        {format === 'html' && active ? (
          customMode ? (
            <div>
              <button type="button" className={skin.button} onClick={() => setPreviewOpen(open => !open)}>
                {previewOpen ? 'Edit HTML' : 'Preview'}
              </button>
              <p className={skin.status}>Paste or replace your HTML. Scripts, embeds and stylesheets are removed; safe inline styles are supported.</p>
              {previewOpen ? <CustomHtmlFrame html={customSource} title="Custom HTML preview" /> : (
                <textarea
                  aria-label="Custom HTML source"
                  className="box-border min-h-[420px] w-full resize-y border-0 bg-white p-4 font-mono text-sm text-slate-900 outline-none"
                  value={customSource}
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                  onChange={event => {
                    const source = event.currentTarget.value
                    if (change(serializeCustomHtmlDocument(source), 'custom-html:input')) setCustomSource(source)
                  }}
                />
              )}
            </div>
          ) : protectsStructuredDocument ? (
            <div className={skin.surfaceWrap}>
              <RichContent html={documentValue} className={skin.surface} />
              <p className={skin.status}>
                This {VARIANT_LABELS[structuredVariant]} document is protected here.
                {structuredVariant === 'contacts'
                  ? ' The experimental Contacts editor is hidden.'
                  : ` Choose ${VARIANT_LABELS[structuredVariant]} to edit it.`}
              </p>
            </div>
          ) : (
            <Suspense fallback={<div className={skin.status}>Loading {selectedVariant} editor…</div>}>
              <Variant {...variantProps} />
            </Suspense>
          )
        ) : null}
      </div>
    </div>
  )
}
